import { useCallback, useEffect, useRef } from 'react';
import Router, { useRouter } from 'next/router';

interface NavigateTo {
  emitPreventedUrl: () => void;
}

interface Props {
  allowChangeRoute: boolean;
  callback: (props: NavigateTo) => void;
}

const usePreventUrlChange = ({
  allowChangeRoute,
  callback,
}: Props) => {
  const router = useRouter();
  const ref = useRef(true);

  const navigateTo = useCallback(
    (link: string): void => {
      ref.current = false;
      router.push(link, undefined, { shallow: true });
    },
    [router]
  );

  const preventRedirect = useCallback(() => {
    if (ref.current) {
      Router.events.emit('routeChangeError');
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw 'Abort route change.';
    }
  }, []);

  const routeChangeStart = useCallback(
    (targetLocation: string) => {
      if (router.pathname === targetLocation) {
        preventRedirect();
      }

      if (ref.current) {
        callback({ emitPreventedUrl: () => navigateTo(targetLocation) });
        preventRedirect();
      }
    },
    [navigateTo, callback, preventRedirect, router]
  );

  const onBeforeHistoryChange = useCallback(() => {
    if (ref.current) {
      preventRedirect();
    }
  }, [preventRedirect]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!allowChangeRoute) {
      Router.events.on('beforeHistoryChange', onBeforeHistoryChange);
      Router.events.on('routeChangeStart', routeChangeStart);

      return () => {
        Router.events.off('beforeHistoryChange', onBeforeHistoryChange);
        Router.events.off('routeChangeStart', routeChangeStart);
      };
    }
  }, [allowChangeRoute, routeChangeStart, onBeforeHistoryChange]);
};

export default usePreventUrlChange;
